import { Injectable, NgZone } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, Subject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { LeadDashboardService } from 'src/app/components/dashboard/lead-dashboard/lead-dashboard.service';

/**
 * New-lead card with a sound (docs/plan-lead-popup.md §3, LP2). The server sends `leadOffer` /
 * `leadOfferClosed` only to Admin sockets (Services/leadPopupService.js, flag LEAD_PANEL_POPUP);
 * side-nav forwards them here. The card shows no customer data - the Lead Dashboard does.
 *
 * Rules: the card stays until Accept / View / Later; one chime per browser (first tab claims it in
 * localStorage), once more after 60 s if still open; own mute key `leadSoundSetting`; Accept or
 * Later closes the card in the other tabs (BroadcastChannel); no card on the Lead Dashboard itself
 * (it refreshes and marks the row instead).
 */
export interface LeadOfferEvent {
  v: number; offerId: string; branchId: string; branchName: string; category: string; area: string;
  distanceKm: number | null; cost: 'free' | 'credit'; freeLeft: number | null; campaign: string;
  creditsLeft: number; sentAt: string; reminder: boolean;
}
export interface LeadOfferClosedEvent { offerId: string; reason: string; }
export type LeadCardState = 'new' | 'confirm' | 'busy' | 'ok' | 'pay' | 'converted' | 'closed' | 'error';
export interface LeadCard {
  offer: LeadOfferEvent;
  state: LeadCardState;
  message?: string;
  soundBlocked?: boolean;   // the browser refused to play before any click
  plays: number;            // chimes played for this card in this tab
  ownsSound: boolean;       // this tab claimed the sound (other tabs stay silent)
}

export const LEAD_DASHBOARD_URL = '/gym/dashboard/lead';
const SOUND_SRC = 'assets/sounds/lead.wav';
const SOUND_KEY = 'leadSoundSetting';         // 'false' = muted; separate from the attendance mute (Q1)
const CLAIM_PREFIX = 'leadSound:';
const CLAIM_KEEP_MS = 24 * 3600 * 1000;
const MAX_PLAYS = 2;
const REPEAT_MS = 60000;
const NAVIGATE_AFTER_MS = 1200;
const CLOSED_HIDE_MS = 5000;

@Injectable({ providedIn: 'root' })
export class LeadPopupService {
  readonly cards$ = new BehaviorSubject<LeadCard[]>([]);
  readonly arrived$ = new Subject<LeadOfferEvent>();          // the Lead Dashboard refreshes and marks the row
  readonly closed$ = new Subject<LeadOfferClosedEvent>();
  readonly sound$ = new BehaviorSubject<boolean>(this.readSoundSetting());
  // Leads tab in the top bar (side-nav): leads still waiting for Accept / Reject (status 'offered')
  readonly pending$ = new BehaviorSubject<number>(0);

  private channel: BroadcastChannel | null = null;
  private readonly timers = new Map<string, any[]>();
  private badgeOn = false;
  private pendingTimer: any;
  private wasOnDashboard = false;
  private anchorEl: HTMLElement | null = null;

  constructor(private router: Router, private api: LeadDashboardService, private zone: NgZone) {
    try {
      this.channel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('fyt-lead') : null;
    } catch (e) { this.channel = null; }
    if (this.channel) {
      this.channel.onmessage = (e: MessageEvent) => this.zone.run(() => {
        if (e.data && e.data.type === 'close' && e.data.offerId) { this.remove(e.data.offerId); }
      });
    }
    // opening the Lead Dashboard shows every lead there - the cards are done
    this.router.events.pipe(filter(ev => ev instanceof NavigationEnd)).subscribe(() => {
      const onDash = this.onDashboard;
      if (onDash) { this.cards$.value.filter(c => !this.isFinishing(c)).forEach(c => this.remove(c.offer.offerId)); }
      // leads may have been accepted / rejected there - recount once on the way out
      if (this.wasOnDashboard && !onDash) { this.refreshPending(0); }
      this.wasOnDashboard = onDash;
    });
  }

  // ---- Leads tab badge (side-nav) ----

  // Called by side-nav only when the Leads tab is shown, so other logins never call /leads/offers.
  enableBadge(): void {
    if (this.badgeOn) { return; }
    this.badgeOn = true;
    this.refreshPending(0);
  }

  // Debounced recount: a burst of socket events costs one request.
  refreshPending(delayMs = 600): void {
    if (!this.badgeOn) { return; }
    clearTimeout(this.pendingTimer);
    this.pendingTimer = setTimeout(() => this.api.offers().subscribe(r => {
      this.pending$.next(r.ok && Array.isArray(r.data) ? r.data.filter(o => o.status === 'offered').length : 0);
    }), delayMs);
  }

  // A card still waiting for the Admin's answer (red dot on the Leads tab).
  static isWaiting(c: LeadCard): boolean {
    return c.state === 'new' || c.state === 'confirm' || c.state === 'busy' || c.state === 'error';
  }

  // The Leads tab element - new cards animate out of it.
  setAnchor(el: HTMLElement | null): void { this.anchorEl = el; }

  anchorRect(): DOMRect | null {
    return this.anchorEl && this.anchorEl.isConnected ? this.anchorEl.getBoundingClientRect() : null;
  }

  get onDashboard(): boolean {
    return ((this.router && this.router.url) || '').split('?')[0].replace(/\/+$/, '').endsWith('/dashboard/lead');
  }

  get soundOn(): boolean { return this.sound$.value; }

  // ---- socket events (from side-nav) ----

  onOffer(o: LeadOfferEvent): void {
    if (!o || !o.offerId) { return; }
    this.arrived$.next(o);
    this.refreshPending();
    const ownsSound = this.claimSound(o.offerId, o.reminder);
    if (this.onDashboard) {
      if (ownsSound) { this.play(null, o.reminder); }
      return;
    }
    const cards = this.cards$.value;
    const old = cards.find(c => c.offer.offerId === o.offerId);
    if (old) {
      if (old.state !== 'new') { return; }      // the Admin is answering this one already
      old.offer = o;
      this.cards$.next([...cards]);
    } else {
      this.cards$.next([...cards, { offer: o, state: 'new', plays: 0, ownsSound }]);
    }
    const card = this.find(o.offerId);
    if (card && card.ownsSound) {
      this.play(card, o.reminder);
      // once more after a minute if nobody answered (never more than twice)
      this.later(o.offerId, () => {
        const c = this.find(o.offerId);
        if (c && (c.state === 'new' || c.state === 'confirm') && c.plays < MAX_PLAYS) { this.play(c, true); }
      }, REPEAT_MS);
    }
  }

  onClosed(e: LeadOfferClosedEvent): void {
    if (!e || !e.offerId) { return; }
    this.closed$.next(e);
    this.refreshPending();
    const c = this.find(e.offerId);
    // this tab's own accept is finishing (green card, then the dashboard) - let it finish
    if (c && !this.isFinishing(c) && c.state !== 'busy') { this.remove(e.offerId); }
  }

  // ---- card actions ----

  accept(c: LeadCard): void {
    if (c.state === 'busy') { return; }
    if (c.offer.cost === 'credit' && c.state !== 'confirm' && c.state !== 'error') {
      this.patch(c, { state: 'confirm', message: '' });      // Q2: one-line confirm when it costs a credit
      return;
    }
    if (c.offer.cost === 'credit' && c.offer.creditsLeft <= 0) {
      this.finish(c, 'pay', 'pay', 'You have no lead credits left. Opening the Lead Dashboard to pay for 1 credit and accept this lead…');
      return;
    }
    this.patch(c, { state: 'busy', message: '' });
    this.api.accept(c.offer.offerId).subscribe(r => {
      if (r.ok) {
        const free = r.data && r.data.charged === 'free';
        const note = free ? 'Accepted free (offer)' : 'Accepted · 1 credit used';
        this.finish(c, 'ok', null, note + '. Opening the Lead Dashboard with the name and mobile number…',
          { leadPhone: r.data && r.data.lead && r.data.lead.phoneNo, leadNote: note });
      } else if (r.code === '007') {
        this.finish(c, 'pay', 'pay', 'You have no lead credits left. Opening the Lead Dashboard to pay for 1 credit and accept this lead…');
      } else if (r.code === '013') {
        this.finish(c, 'converted', 'confirm', 'This person may have already joined a centre. Opening the Lead Dashboard so you can decide…',
          { convertedMessage: r.message });
      } else if (r.code === '008') {
        this.patch(c, { state: 'closed', message: 'This lead is no longer available.' });
        this.broadcastClose(c.offer.offerId);
        this.later(c.offer.offerId, () => this.remove(c.offer.offerId), CLOSED_HIDE_MS);
      } else {
        this.patch(c, { state: 'error', message: r.message || 'Could not accept. Please check the internet connection and try again.' });
      }
    }, () => this.patch(c, { state: 'error', message: 'Could not accept. Please check the internet connection and try again.' }));
  }

  cancelConfirm(c: LeadCard): void { this.patch(c, { state: 'new', message: '' }); }

  view(c: LeadCard): void {
    this.remove(c.offer.offerId);
    this.goToDashboard(c.offer.offerId, null);
  }

  // hide here and in the other tabs; the lead stays in the Lead Dashboard
  dismiss(c: LeadCard): void {
    this.remove(c.offer.offerId);
    this.broadcastClose(c.offer.offerId);
  }

  openDashboard(): void { this.router.navigate([LEAD_DASHBOARD_URL]); }

  toggleSound(): void {
    const on = !this.soundOn;
    try { localStorage.setItem(SOUND_KEY, on ? 'true' : 'false'); } catch (e) { }
    this.sound$.next(on);
  }

  // "Tap to turn on sound": a click lets the browser play
  enableSound(c: LeadCard): void {
    this.patch(c, { soundBlocked: false });
    if (!this.soundOn) { this.toggleSound(); }
    this.play(c, false);
  }

  // ---- internals ----

  private finish(c: LeadCard, state: LeadCardState, action: 'pay' | 'confirm' | null, message: string, navState?: any): void {
    this.patch(c, { state, message });
    this.broadcastClose(c.offer.offerId);
    this.refreshPending();
    this.later(c.offer.offerId, () => {
      this.remove(c.offer.offerId);
      this.goToDashboard(c.offer.offerId, action, navState);
    }, NAVIGATE_AFTER_MS);
  }

  private goToDashboard(offerId: string, action: 'pay' | 'confirm' | null, navState?: any): void {
    const queryParams: any = { offer: offerId };
    if (action) { queryParams.action = action; }
    this.router.navigate([LEAD_DASHBOARD_URL], { queryParams, state: navState || {} });
  }

  private isFinishing(c: LeadCard): boolean { return c.state === 'ok' || c.state === 'pay' || c.state === 'converted'; }

  private find(offerId: string): LeadCard | undefined { return this.cards$.value.find(c => c.offer.offerId === offerId); }

  private patch(c: LeadCard, change: Partial<LeadCard>): void {
    const cur = this.find(c.offer.offerId);
    if (!cur) { return; }
    Object.assign(cur, change);
    this.cards$.next([...this.cards$.value]);
  }

  private remove(offerId: string): void {
    (this.timers.get(offerId) || []).forEach(t => clearTimeout(t));
    this.timers.delete(offerId);
    const rest = this.cards$.value.filter(c => c.offer.offerId !== offerId);
    if (rest.length !== this.cards$.value.length) { this.cards$.next(rest); }
  }

  private later(offerId: string, fn: () => void, ms: number): void {
    const t = setTimeout(() => this.zone.run(fn), ms);
    this.timers.set(offerId, [...(this.timers.get(offerId) || []), t]);
  }

  private broadcastClose(offerId: string): void {
    try { if (this.channel) { this.channel.postMessage({ type: 'close', offerId }); } } catch (e) { }
  }

  private readSoundSetting(): boolean {
    try { return localStorage.getItem(SOUND_KEY) !== 'false'; } catch (e) { return true; }
  }

  // first tab of this browser to see the lead plays the sound; the others stay silent
  private claimSound(offerId: string, reminder: boolean): boolean {
    const key = CLAIM_PREFIX + offerId + (reminder ? ':r' : '');
    try {
      if (localStorage.getItem(key)) { return false; }
      localStorage.setItem(key, String(Date.now()));
      this.pruneClaims();
    } catch (e) { /* no storage: play in this tab */ }
    return true;
  }

  private pruneClaims(): void {
    const now = Date.now();
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (k && k.startsWith(CLAIM_PREFIX) && now - Number(localStorage.getItem(k)) > CLAIM_KEEP_MS) { localStorage.removeItem(k); }
    }
  }

  private play(c: LeadCard | null, soft: boolean): void {
    if (!this.soundOn) { return; }
    if (c) { this.patch(c, { plays: c.plays + 1 }); }
    try {
      const audio = new Audio(SOUND_SRC);
      audio.volume = soft ? 0.6 : 1;
      const p = audio.play();
      if (p && typeof p.catch === 'function') {
        p.catch(() => this.zone.run(() => { if (c) { this.patch(c, { soundBlocked: true }); } }));
      }
    } catch (e) { if (c) { this.patch(c, { soundBlocked: true }); } }
  }
}
